import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { PDF_BODY_CLASS, PDF_DESC_CLASS, PDF_EYEBROW_CLASS } from "@/lib/proposalPdfTypography";
import {
  resolvePdfProductDisplayName,
  resolvePdfProductExperienceSummary,
  resolvePdfProductWhyItMatters,
} from "@/lib/proposalPdfProductDisplay";
import { parseDtoolsLineCatalogMeta, resolveLineDisplayImage } from "@shared/dtoolsCatalogueMatch";
import { formatProductBrandModelReference } from "@shared/productDisplayName";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";

type Props = {
  line: DtoolsStagingLine;
  hidePrice?: boolean;
  priceFormatted?: string;
};

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

/** Compact primary product row for PDF — mockup list-row density. */
export default function DtoolsPdfPrimaryProductRow({ line, hidePrice = true, priceFormatted }: Props) {
  const displayName = resolvePdfProductDisplayName(line);
  const description = resolvePdfProductExperienceSummary(line, displayName);
  const whyItMatters = resolvePdfProductWhyItMatters(line);
  const showWhyItMatters =
    whyItMatters && (!description || whyItMatters.toLowerCase() !== description.toLowerCase());
  const modelRef = formatProductBrandModelReference({
    brand: line.manufacturer,
    model: line.model,
  });
  const showModelRef = modelRef && modelRef.toLowerCase() !== displayName.toLowerCase();
  const catalogMeta = parseDtoolsLineCatalogMeta(line);
  const imageUrl = clean(resolveLineDisplayImage(line.imageUrl, catalogMeta));

  return (
    <div
      className="pdf-keep-together flex items-start gap-3 border-b border-[#1A2744]/10 py-2.5"
      data-pdf-pagination-unit="true"
      data-testid="dtools-pdf-primary-product-row"
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      <div className="flex w-[148px] shrink-0 items-center justify-center rounded-md bg-[#FAFAF7] p-2">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-[96px] w-[132px] max-w-full object-contain object-center"
            loading="eager"
            decoding="sync"
            crossOrigin="anonymous"
            onError={(event) => {
              const img = event.currentTarget;
              img.style.visibility = "hidden";
              img.style.opacity = "0";
              img.setAttribute("data-pdf-image-failed", "true");
            }}
          />
        ) : (
          <div className="h-[96px] w-[132px]" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-[13px] font-semibold leading-snug text-[#1A2744]">{displayName}</p>
        {showModelRef ? (
          <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#6B7280]">{modelRef}</p>
        ) : null}
        {showWhyItMatters ? (
          <div className="space-y-0.5 pt-0.5">
            <p className={PDF_EYEBROW_CLASS}>Why it matters</p>
            <p className={`${PDF_BODY_CLASS} leading-snug text-[#1A2744]`}>{whyItMatters}</p>
          </div>
        ) : null}
        {description ? <p className={`${PDF_DESC_CLASS} line-clamp-3`}>{description}</p> : null}
        {!hidePrice && priceFormatted ? (
          <p className={`${PDF_BODY_CLASS} font-semibold text-[#1A2744]`}>{priceFormatted}</p>
        ) : null}
      </div>

      <div className={`w-12 shrink-0 pt-1 text-right tabular-nums text-[#1A2744] ${PDF_BODY_CLASS}`}>
        <span className="block text-[9px] uppercase tracking-wide text-[#6B7280]">Qty</span>
        <span className="font-semibold">{line.quantity}</span>
      </div>
    </div>
  );
}
